<div class="container-fluid complyon-page command-centre-page px-3 px-md-4 py-3">
  <div class="complyon-shell command-centre-shell mx-auto">
    <section class="command-centre-header mb-3">
      <h1 class="command-centre-title mb-2">Command centre</h1>
      <p class="command-centre-subtitle mb-0">
        The executive control view - compliance health, audit readiness, open risks and the signals that answer: are we in control today?
      </p>
    </section>

    <section class="command-centre-hero-card command-centre-panel rounded-4 mb-3">
      <div class="row g-4 align-items-center">
        <div class="col-12 col-xl-7">
          <div class=" text-s mb-3">ARE WE IN CONTROL TODAY?</div>
          <div class="d-flex align-items-start gap-3">
            <span class="command-centre-alert-dot flex-shrink-0"></span>
            <div class="min-w-0">
              <h4 class="fw-bold mb-2">Attention required</h4>
              <p class="command-centre-hero-copy mb-3">
                Multiple control gaps are open. Prioritise the red signals today.
              </p>
              <div class="d-flex flex-wrap gap-2">
                <button type="button" class="btn command-centre-primary-btn d-inline-flex align-items-center gap-2">
                  <i class="bi bi-download"></i>
                  <span>Export board brief</span>
                </button>
                <button type="button" class="btn command-centre-secondary-btn d-inline-flex align-items-center gap-2">
                  <i class="bi bi-file-earmark-text"></i>
                  <span>Full report</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-5">
          <div class="command-centre-hero-rings">
            <div
              v-for="ring in hero_rings"
              :key="ring.label"
              class="command-centre-ring-card"
            >
              <div class="command-centre-ring" :style="{ '--ring-value': ring.value, '--ring-color': ring.color }">
                <div class="command-centre-ring-inner">
                  <div class="command-centre-ring-value">{{ ring.value }}</div>
                  <div class="command-centre-ring-unit">%</div>
                </div>
              </div>
              <div class="command-centre-ring-label">{{ ring.label }}</div>
              <div class="command-centre-ring-subtitle">{{ ring.subtitle }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mb-3">
      <div class="command-centre-section-eyebrow mb-2">CONTROL SIGNALS</div>
      <div class="row g-2 g-md-3">
        <div
          v-for="card in control_signals"
          :key="card.label"
          class="col-12 col-sm-6 col-xl-2"
        >
          <article
            class="command-centre-signal-card rounded-4 p-3 h-100"
            :class="`is-${card.tone}`"
            role="button"
            tabindex="0"
            @click="open_control_signal(card)"
            @keyup.enter.prevent="open_control_signal(card)"
            @keyup.space.prevent="open_control_signal(card)"
          >
            <div class="command-centre-signal-value mb-1" :class="`is-${card.tone}`">{{ card.value }}</div>
            <div class="command-centre-signal-title">{{ card.label }}</div>
            <div class="command-centre-signal-subtitle">{{ card.subtitle }}</div>
          </article>
        </div>
      </div>
    </section>

    <section class="row g-3 mb-3">
      <div class="col-12 col-xl-7">
        <article class="command-centre-panel command-centre-focus-panel rounded-4 h-100">
          <div class="command-centre-panel-head d-flex align-items-start justify-content-between gap-3">
            <div>
              <h3 class="command-centre-section-title mb-1">Needs you today</h3>
              <p class="command-centre-section-subtitle mb-0">
                The highest-priority control gaps, aggregated across every domain.
              </p>
            </div>
            <span class="command-centre-count-pill d-inline-flex align-items-center gap-1">
              <span class="command-centre-count-dot"></span>
              <span>6 items</span>
            </span>
          </div>

          <div class="command-centre-needs-list">
            <div
              v-for="item in needs_today_items"
              :key="item.title"
              class="command-centre-need-row d-flex align-items-center justify-content-between gap-3"
              role="button"
              tabindex="0"
              @click="open_needs_today_item(item)"
              @keyup.enter.prevent="open_needs_today_item(item)"
              @keyup.space.prevent="open_needs_today_item(item)"
            >
              <div class="d-flex align-items-start gap-3 min-w-0">
                <span class="command-centre-need-icon d-inline-flex align-items-center justify-content-center" :class="item.icon_class">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <div class="min-w-0">
                  <div class="command-centre-need-title">{{ item.title }}</div>
                  <div class="command-centre-need-subtitle">{{ item.subtitle }}</div>
                </div>
              </div>
              <i class="bi bi-chevron-right command-centre-need-chevron"></i>
            </div>
          </div>
        </article>
      </div>

      <div class="col-12 col-xl-5">
        <article class="command-centre-panel command-centre-focus-panel rounded-4 h-100">
          <div class="command-centre-panel-head">
            <h3 class="command-centre-section-title mb-1">Health by domain</h3>
            <p class="command-centre-section-subtitle mb-0">What drives the composite</p>
          </div>

          <div class="command-centre-domain-list">
            <div
              v-for="domain in domain_health_rows"
              :key="domain.label"
              class="command-centre-domain-row"
              role="button"
              tabindex="0"
              @click="open_domain_health_item(domain)"
              @keyup.enter.prevent="open_domain_health_item(domain)"
              @keyup.space.prevent="open_domain_health_item(domain)"
            >
              <div class="command-centre-domain-label">{{ domain.label }}</div>
              <div class="command-centre-domain-bar-wrap">
                <div class="progress command-centre-domain-track">
                  <div
                    class="progress-bar command-centre-domain-bar"
                    :class="`is-${domain.tone}`"
                    :style="{ width: `${domain.value}%` }"
                  ></div>
                </div>
              </div>
              <div class="command-centre-domain-values">
                <span>{{ domain.value }}%</span>
                <span class="text-secondary">{{ domain.target }}%</span>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="command-centre-panel rounded-4 mb-3">
      <div class="command-centre-panel-head">
        <h3 class="command-centre-section-title mb-1">Department compliance performance</h3>
        <p class="command-centre-section-subtitle mb-0">
          Live employee-compliance score by department - click to drill in.
        </p>
      </div>

      <div class="row g-3 p-3">
        <div
          v-for="department in department_cards"
          :key="department.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article
            class="command-centre-department-card rounded-4 p-3 h-100"
            role="button"
            tabindex="0"
            @click="open_department_card(department)"
            @keyup.enter.prevent="open_department_card(department)"
            @keyup.space.prevent="open_department_card(department)"
          >
            <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
              <div class="command-centre-department-title">{{ department.label }}</div>
              <div class="command-centre-department-score" :class="`is-${department.tone}`">{{ department.value }}%</div>
            </div>
            <div class="progress command-centre-department-track mb-2">
              <div
                class="progress-bar command-centre-department-bar"
                :class="`is-${department.tone}`"
                :style="{ width: `${department.value}%` }"
              ></div>
            </div>
            <div class="command-centre-department-meta">{{ department.meta }}</div>
          </article>
        </div>
      </div>
    </section>
  </div>
</div>
